import React, { useEffect, useRef } from 'react';

class MergeSystemManager {
  constructor() {
    this.shapes = [];
    this.draggedShape = null;
    this.scaledShape = null; // Track shape being scaled
    this.snapThreshold = 50; // pixels
    this.isActive = false;
    this.isScaling = false; // Track if we're in scaling mode
    this.listeners = [];
  }

  setShapes(shapes) {
    this.shapes = shapes;
    console.log('🔄 MERGE SYSTEM SHAPES UPDATED:', {
      totalShapes: shapes.length,
      rectangles: shapes.filter(s => s.type === 'rectangle').length,
      mergedStructures: shapes.filter(s => s.type === 'mergedStructure').length,
      shapes: shapes.map(s => ({
        id: s.id,
        type: s.type,
        bounds: s.type === 'rectangle' || s.type === 'mergedStructure' ? 
          { x: s.x, y: s.y, width: s.width, height: s.height } : 'not rectangle'
      }))
    });
  }

  startDrag(shape) {
    this.draggedShape = shape;
    this.isActive = true;
    this.isScaling = false;
  }

  startScale(shape) {
    this.scaledShape = shape;
    this.isActive = true;
    this.isScaling = true;
  }

  stopDrag() {
    this.draggedShape = null;
    this.isActive = false;
    this.isScaling = false;
  }

  stopScale() {
    this.scaledShape = null;
    this.isActive = false;
    this.isScaling = false;
  }

  // Calculate distance between two rectangle edges
  calculateEdgeDistance(rect1, rect2) {
    const edges1 = this.getRectangleEdges(rect1);
    const edges2 = this.getRectangleEdges(rect2);
    
    let minDistance = Infinity;
    let snapInfo = null;

    // Check all edge combinations
    for (const edge1 of edges1) {
      for (const edge2 of edges2) {
        if (this.areEdgesCompatible(edge1, edge2)) {
          const distance = this.getEdgeDistance(edge1, edge2);
          if (distance < minDistance) {
            minDistance = distance;
            snapInfo = {
              edge1,
              edge2,
              distance,
              snapType: this.getSnapType(edge1, edge2)
            };
          }
        }
      }
    }

    return { distance: minDistance, snapInfo };
  }

  // Get rectangle edges with their positions and orientations
  getRectangleEdges(rect) {
    const { x, y, width, height } = rect;
    
    return [
      // Top edge
      {
        type: 'top',
        start: { x, y },
        end: { x: x + width, y },
        orientation: 'horizontal'
      },
      // Right edge
      {
        type: 'right',
        start: { x: x + width, y },
        end: { x: x + width, y: y + height },
        orientation: 'vertical'
      },
      // Bottom edge
      {
        type: 'bottom',
        start: { x: x + width, y: y + height },
        end: { x, y: y + height },
        orientation: 'horizontal'
      },
      // Left edge
      {
        type: 'left',
        start: { x, y: y + height },
        end: { x, y },
        orientation: 'vertical'
      }
    ];
  }

  // Check if two edges can snap together
  areEdgesCompatible(edge1, edge2) {
    // Edges must have same orientation and be opposite types
    if (edge1.orientation !== edge2.orientation) return false;
    
    const opposites = {
      'top': 'bottom',
      'bottom': 'top',
      'left': 'right',
      'right': 'left'
    };
    
    return opposites[edge1.type] === edge2.type;
  }

  // Calculate distance between two edges
  getEdgeDistance(edge1, edge2) {
    if (edge1.orientation === 'horizontal') {
      // Check if edges overlap horizontally
      const overlap = this.getLineOverlap(
        edge1.start.x, edge1.end.x,
        edge2.start.x, edge2.end.x
      );
      
      if (overlap > 0) {
        return Math.abs(edge1.start.y - edge2.start.y);
      }
    } else {
      // Check if edges overlap vertically
      const overlap = this.getLineOverlap(
        edge1.start.y, edge1.end.y,
        edge2.start.y, edge2.end.y
      );
      
      if (overlap > 0) {
        return Math.abs(edge1.start.x - edge2.start.x);
      }
    }
    
    return Infinity; // No overlap
  }

  // Calculate overlap between two line segments
  getLineOverlap(start1, end1, start2, end2) {
    const min1 = Math.min(start1, end1);
    const max1 = Math.max(start1, end1);
    const min2 = Math.min(start2, end2);
    const max2 = Math.max(start2, end2);
    
    const overlapStart = Math.max(min1, min2);
    const overlapEnd = Math.min(max1, max2);
    
    return Math.max(0, overlapEnd - overlapStart);
  }

  getSnapType(edge1, edge2) {
    return `${edge1.type}-to-${edge2.type}`;
  }

  // Check for potential merges during drag or scale
  checkForMergeOpportunities(activeShape) {
    if (!this.isActive || !activeShape) return null;

    // Use current active shape (either dragged or scaled)
    const currentShape = activeShape;

    console.log('🔍 CHECKING MERGE OPPORTUNITIES:', {
      activeShapeId: currentShape.id,
      activeShapeType: currentShape.type,
      totalShapes: this.shapes.length,
      shapeTypes: this.shapes.map(s => ({ id: s.id, type: s.type }))
    });

    for (const shape of this.shapes) {
      if (shape.id === currentShape.id) continue;
      
      // Handle both individual rectangles and merged structures
      if (shape.type !== 'rectangle' && shape.type !== 'mergedStructure') continue;

      console.log('🎯 TESTING MERGE WITH:', {
        targetShapeId: shape.id,
        targetShapeType: shape.type,
        targetBounds: { x: shape.x, y: shape.y, width: shape.width, height: shape.height }
      });

      const { distance, snapInfo } = this.calculateEdgeDistance(currentShape, shape);
      
      console.log('📏 DISTANCE CALCULATION:', {
        distance,
        snapThreshold: this.snapThreshold,
        canSnap: distance <= this.snapThreshold,
        snapInfo
      });
      
      if (distance <= this.snapThreshold) {
        console.log('✅ MERGE OPPORTUNITY FOUND!', {
          targetShape: shape,
          distance,
          snapInfo,
          mergeType: shape.type === 'mergedStructure' ? 'addToStructure' : 'createNewStructure'
        });
        
        return {
          targetShape: shape,
          distance,
          snapInfo,
          canMerge: true,
          mergeType: shape.type === 'mergedStructure' ? 'addToStructure' : 'createNewStructure',
          operationType: this.isScaling ? 'scaling' : 'dragging' // Track operation type
        };
      }
    }

    console.log('❌ NO MERGE OPPORTUNITIES FOUND');
    return null;
  }

  // Calculate snap position for merging
  calculateSnapPosition(draggedShape, targetShape, snapInfo) {
    const { edge1, edge2, snapType } = snapInfo;
    let newPosition = { x: draggedShape.x, y: draggedShape.y };

    // Use the target shape's bounding box (works for both rectangles and merged structures)
    const targetBounds = {
      x: targetShape.x,
      y: targetShape.y,
      width: targetShape.width,
      height: targetShape.height
    };

    if (edge1.orientation === 'horizontal') {
      // Horizontal snapping - edges should touch vertically
      if (snapType.includes('top-to-bottom')) {
        // Dragged shape's top edge snaps to target's bottom edge
        newPosition.y = targetBounds.y + targetBounds.height;
      } else if (snapType.includes('bottom-to-top')) {
        // Dragged shape's bottom edge snaps to target's top edge
        newPosition.y = targetBounds.y - draggedShape.height;
      }
      
      // Align horizontally for optimal overlap while maintaining some overlap
      const draggedLeft = draggedShape.x;
      const draggedRight = draggedShape.x + draggedShape.width;
      const targetLeft = targetBounds.x;
      const targetRight = targetBounds.x + targetBounds.width;
      
      // Calculate current overlap
      const overlapLeft = Math.max(draggedLeft, targetLeft);
      const overlapRight = Math.min(draggedRight, targetRight);
      const currentOverlap = Math.max(0, overlapRight - overlapLeft);
      
      // If there's minimal overlap, align centers for better connection
      if (currentOverlap < Math.min(draggedShape.width, targetBounds.width) * 0.3) {
        const draggedCenter = draggedShape.x + draggedShape.width / 2;
        const targetCenter = targetBounds.x + targetBounds.width / 2;
        newPosition.x = targetCenter - draggedShape.width / 2;
      }
      // Otherwise keep current x position for good overlap
      
    } else {
      // Vertical snapping - edges should touch horizontally
      if (snapType.includes('left-to-right')) {
        // Dragged shape's left edge snaps to target's right edge
        newPosition.x = targetBounds.x + targetBounds.width;
      } else if (snapType.includes('right-to-left')) {
        // Dragged shape's right edge snaps to target's left edge
        newPosition.x = targetBounds.x - draggedShape.width;
      }
      
      // Align vertically for optimal overlap while maintaining some overlap
      const draggedTop = draggedShape.y;
      const draggedBottom = draggedShape.y + draggedShape.height;
      const targetTop = targetBounds.y;
      const targetBottom = targetBounds.y + targetBounds.height;
      
      // Calculate current overlap
      const overlapTop = Math.max(draggedTop, targetTop);
      const overlapBottom = Math.min(draggedBottom, targetBottom);
      const currentOverlap = Math.max(0, overlapBottom - overlapTop);
      
      // If there's minimal overlap, align centers for better connection
      if (currentOverlap < Math.min(draggedShape.height, targetBounds.height) * 0.3) {
        const draggedCenter = draggedShape.y + draggedShape.height / 2;
        const targetCenter = targetBounds.y + targetBounds.height / 2;
        newPosition.y = targetCenter - draggedShape.height / 2;
      }
      // Otherwise keep current y position for good overlap
    }

    console.log('📐 SNAP POSITION CALCULATED:', {
      snapType,
      mergeType: targetShape.type === 'mergedStructure' ? 'ADD_TO_STRUCTURE' : 'CREATE_NEW_STRUCTURE',
      draggedShape: { id: draggedShape.id, original: { x: draggedShape.x, y: draggedShape.y } },
      targetShape: { 
        id: targetShape.id, 
        type: targetShape.type,
        shapesCount: targetShape.shapesCount || 1,
        bounds: targetBounds 
      },
      newPosition,
      edgeAlignment: edge1.orientation === 'horizontal' ? 'vertical-snap' : 'horizontal-snap'
    });

    return newPosition;
  }

  // Add listener for merge events
  addListener(callback) {
    this.listeners.push(callback);
  }

  removeListener(callback) {
    this.listeners = this.listeners.filter(l => l !== callback);
  }

  notifyListeners(event, data) {
    this.listeners.forEach(listener => listener(event, data));
  }
}

// Singleton instance
export const mergeSystemManager = new MergeSystemManager();

// React component wrapper
const MergeSystem = ({ shapes, onMergeRequest }) => {
  const managerRef = useRef(mergeSystemManager);

  useEffect(() => {
    const manager = managerRef.current;
    manager.setShapes(shapes);

    const handleMergeEvent = (event, data) => {
      if (event === 'mergeRequest' && onMergeRequest) {
        onMergeRequest(data);
      }
    };

    manager.addListener(handleMergeEvent);

    return () => {
      manager.removeListener(handleMergeEvent);
    };
  }, [shapes, onMergeRequest]);

  // Listen for shape drag and scaling events
  useEffect(() => {
    const handleShapeDragStart = (event) => {
      const { shapeId } = event.detail;
      const shape = shapes.find(s => s.id === shapeId);
      if (shape && shape.type === 'rectangle') {
        managerRef.current.startDrag(shape);
      }
    };

    const handleShapeDragUpdate = (event) => {
      const { shapeId, position } = event.detail;
      const shape = shapes.find(s => s.id === shapeId);
      
      if (shape && managerRef.current.draggedShape?.id === shapeId) {
        // Update dragged shape position
        const updatedShape = { ...shape, x: position.x, y: position.y };
        
        // Check for merge opportunities
        const mergeOpportunity = managerRef.current.checkForMergeOpportunities(updatedShape);
        
        if (mergeOpportunity) {
          // Instead of dispatching a merge popup, immediately perform the merge
          if (onMergeRequest) {
            onMergeRequest({
              draggedShape: updatedShape,
              targetShape: mergeOpportunity.targetShape,
              snapInfo: mergeOpportunity.snapInfo,
              snapPosition: managerRef.current.calculateSnapPosition(
                updatedShape,
                mergeOpportunity.targetShape,
                mergeOpportunity.snapInfo
              ),
              operationType: 'dragging'
            });
            }
        }
      }
    };

    const handleShapeDragEnd = (event) => {
      managerRef.current.stopDrag();
      // Hide any active merge popup
      window.dispatchEvent(new CustomEvent('hideMergePopup'));
    };

    // SCALING EVENT HANDLERS
    const handleShapeScaleStart = (event) => {
      const { shapeId } = event.detail;
      const shape = shapes.find(s => s.id === shapeId);
      if (shape && shape.type === 'rectangle') {
        managerRef.current.startScale(shape);
      }
    };

    const handleShapeScaleUpdate = (event) => {
      const { shapeId, scaledShape } = event.detail;
      const shape = shapes.find(s => s.id === shapeId);
      
      if (shape && managerRef.current.scaledShape?.id === shapeId) {
        // Check for merge opportunities during scaling
        const mergeOpportunity = managerRef.current.checkForMergeOpportunities(scaledShape);
        
        if (mergeOpportunity) {
          // Instead of dispatching a merge popup, immediately perform the merge
          if (onMergeRequest) {
            onMergeRequest({
              draggedShape: scaledShape, // Use scaledShape but call it draggedShape for compatibility
              targetShape: mergeOpportunity.targetShape,
              snapInfo: mergeOpportunity.snapInfo,
              snapPosition: managerRef.current.calculateSnapPosition(
                scaledShape,
                mergeOpportunity.targetShape,
                mergeOpportunity.snapInfo
              ),
              operationType: 'scaling'
            });
            }
        }
      }
    };

    const handleShapeScaleEnd = (event) => {
      managerRef.current.stopScale();
      // Hide any active merge popup
      window.dispatchEvent(new CustomEvent('hideMergePopup'));
    };

    // Add all event listeners
    window.addEventListener('shapeDragStart', handleShapeDragStart);
    window.addEventListener('shapeDragUpdate', handleShapeDragUpdate);
    window.addEventListener('shapeDragEnd', handleShapeDragEnd);
    window.addEventListener('shapeScaleStart', handleShapeScaleStart);
    window.addEventListener('shapeScaleUpdate', handleShapeScaleUpdate);
    window.addEventListener('shapeScaleEnd', handleShapeScaleEnd);

    return () => {
      window.removeEventListener('shapeDragStart', handleShapeDragStart);
      window.removeEventListener('shapeDragUpdate', handleShapeDragUpdate);
      window.removeEventListener('shapeDragEnd', handleShapeDragEnd);
      window.removeEventListener('shapeScaleStart', handleShapeScaleStart);
      window.removeEventListener('shapeScaleUpdate', handleShapeScaleUpdate);
      window.removeEventListener('shapeScaleEnd', handleShapeScaleEnd);
    };
  }, [shapes]);

  return null; // This component doesn't render anything
};

export default MergeSystem; 